import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_PATH } from '../../apiConfig';
import { runRequestJson } from './appRunData';

export const MAX_CONTEXT_FILE_BYTES = 25 * 1024 * 1024;

export type AppContextSection = 'overview' | 'boundaries' | 'preconditions' | 'assumptions' | 'documentation';

/** Whether evidence backs a precondition or trust assumption; entries start unverified. */
export type AppContextStatus = 'unverified' | 'verified' | 'disproven';

/** Sections whose entries are claims to verify rather than descriptions. */
export const VERIFIABLE_SECTIONS: AppContextSection[] = ['preconditions', 'assumptions'];

export interface AppContextFile {
  name: string;
  size: number;
  type: string;
  path: string;
}

export interface AppContextEntry {
  id: string;
  section: AppContextSection;
  /** A statement, a link or a note; a document's file name. */
  text: string;
  /** Set for documents stored by the workspace backend. */
  file?: AppContextFile;
  status?: AppContextStatus;
  createdAt: string;
}

type ContextMap = Record<string, AppContextEntry[]>;

interface WorkspaceContextResponse {
  context: {
    version: 1;
    entries: AppContextEntry[];
    updatedAt: string;
  };
}

interface ContextDocumentUploadResponse {
  file: AppContextFile;
}

export interface AppContextStore {
  getEntries: (workspaceId: string) => AppContextEntry[];
  getError: (workspaceId: string) => string;
  isReady: (workspaceId: string) => boolean;
  addEntry: (workspaceId: string, section: AppContextSection, text: string) => void;
  updateEntry: (workspaceId: string, entryId: string, change: Partial<Pick<AppContextEntry, 'text' | 'status'>>) => void;
  removeEntry: (workspaceId: string, entryId: string) => void;
  /** Stores documents in the workspace backend and returns an error for files that could not be added. */
  addFiles: (workspaceId: string, files: File[]) => Promise<string>;
  openFile: (workspaceId: string, entry: AppContextEntry) => Promise<void>;
  clearWorkspace: (workspaceId: string) => void;
}

const createEntryId = (): string => typeof window.crypto?.randomUUID === 'function'
  ? window.crypto.randomUUID()
  : `context-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const getResponseError = async (response: Response, fallback: string): Promise<Error> => {
  const payload = await response.json().catch(() => undefined) as { error?: unknown } | undefined;

  return new Error(typeof payload?.error === 'string' && payload.error.trim() ? payload.error : fallback);
};

export const isContextLink = (text: string): boolean => /^https?:\/\/\S+$/i.test(text.trim());

/** Workspace context is loaded from and persisted to the backend; the browser holds only live UI state. */
export function useWorkspaceContext(workspaceIds: string[], isSignedIn: boolean): AppContextStore {
  const [context, setContext] = useState<ContextMap>({});
  const contextRef = useRef<ContextMap>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [readyWorkspaceIds, setReadyWorkspaceIds] = useState<Set<string>>(() => new Set());
  const loadedWorkspaceIdsRef = useRef<Set<string>>(new Set());
  const saveQueuesRef = useRef<Map<string, Promise<void>>>(new Map());
  const workspaceIdsKey = workspaceIds.join('\n');

  const applyEntries = useCallback((workspaceId: string, entries: AppContextEntry[]) => {
    const next = { ...contextRef.current, [workspaceId]: entries };

    contextRef.current = next;
    setContext(next);
  }, []);

  const trackWorkspaceOperation = useCallback((workspaceId: string, operation: Promise<void>) => {
    saveQueuesRef.current.set(workspaceId, operation);
    void operation
      .catch((error: unknown) => {
        console.error('Failed to save workspace context:', error);
        setErrors((current) => ({
          ...current,
          [workspaceId]: 'Changes could not be saved. Check your connection and try again.'
        }));
      })
      .finally(() => {
        if (saveQueuesRef.current.get(workspaceId) === operation) {
          saveQueuesRef.current.delete(workspaceId);
        }
      });
  }, []);

  const enqueueBackendSave = useCallback((workspaceId: string, entries: AppContextEntry[]): Promise<void> => {
    const previous = saveQueuesRef.current.get(workspaceId) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(async () => {
        await runRequestJson<WorkspaceContextResponse>(
          `/workspaces/${encodeURIComponent(workspaceId)}/context`,
          {
            method: 'PUT',
            body: JSON.stringify({ version: 1, entries })
          }
        );
        setErrors((current) => ({ ...current, [workspaceId]: '' }));
      });

    trackWorkspaceOperation(workspaceId, next);
    return next;
  }, [trackWorkspaceOperation]);

  useEffect(() => {
    const activeWorkspaceIds = workspaceIdsKey ? workspaceIdsKey.split('\n') : [];

    if (!isSignedIn) {
      return;
    }

    for (const workspaceId of activeWorkspaceIds) {
      if (loadedWorkspaceIdsRef.current.has(workspaceId)) {
        continue;
      }

      loadedWorkspaceIdsRef.current.add(workspaceId);
      void runRequestJson<WorkspaceContextResponse>(`/workspaces/${encodeURIComponent(workspaceId)}/context`)
        .then((response) => {
          applyEntries(workspaceId, response.context.entries ?? []);
          setErrors((current) => ({ ...current, [workspaceId]: '' }));
        })
        .catch((error: unknown) => {
          console.error('Failed to load workspace context:', error);
          setErrors((current) => ({
            ...current,
            [workspaceId]: 'Workspace context could not be loaded. Check your connection and try again.'
          }));
        })
        .finally(() => {
          setReadyWorkspaceIds((current) => new Set(current).add(workspaceId));
        });
    }
  }, [applyEntries, isSignedIn, workspaceIdsKey]);

  const update = useCallback((workspaceId: string, change: (entries: AppContextEntry[]) => AppContextEntry[]) => {
    const entries = change(contextRef.current[workspaceId] ?? []);

    applyEntries(workspaceId, entries);

    if (isSignedIn) {
      void enqueueBackendSave(workspaceId, entries);
    }
  }, [applyEntries, enqueueBackendSave, isSignedIn]);

  const getEntries = useCallback((workspaceId: string) => context[workspaceId] ?? [], [context]);
  const getError = useCallback((workspaceId: string) => errors[workspaceId] ?? '', [errors]);
  const isReady = useCallback((workspaceId: string) => readyWorkspaceIds.has(workspaceId), [readyWorkspaceIds]);

  const addEntry = useCallback((workspaceId: string, section: AppContextSection, text: string) => {
    update(workspaceId, (entries) => [
      ...entries,
      { id: createEntryId(), section, text, createdAt: new Date().toISOString() }
    ]);
  }, [update]);

  const updateEntry = useCallback((
    workspaceId: string,
    entryId: string,
    change: Partial<Pick<AppContextEntry, 'text' | 'status'>>
  ) => {
    update(workspaceId, (entries) => entries.map((entry) => entry.id === entryId ? { ...entry, ...change } : entry));
  }, [update]);

  const removeEntry = useCallback((workspaceId: string, entryId: string) => {
    update(workspaceId, (entries) => entries.filter((entry) => entry.id !== entryId));
  }, [update]);

  const uploadFile = useCallback(async (
    workspaceId: string,
    entryId: string,
    file: File
  ): Promise<AppContextFile> => {
    const previous = saveQueuesRef.current.get(workspaceId) ?? Promise.resolve();
    let uploadedFile: AppContextFile | undefined;
    const upload = previous
      .catch(() => undefined)
      .then(async () => {
        const query = new URLSearchParams({ name: file.name, type: file.type });
        const response = await fetch(
          `${API_BASE_PATH}/workspaces/${encodeURIComponent(workspaceId)}/context/documents/${encodeURIComponent(entryId)}?${query.toString()}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/octet-stream' },
            body: file
          }
        );

        if (!response.ok) {
          throw await getResponseError(response, 'The context document could not be uploaded.');
        }

        uploadedFile = (await response.json() as ContextDocumentUploadResponse).file;
      });

    trackWorkspaceOperation(workspaceId, upload);
    await upload;

    if (!uploadedFile) {
      throw new Error('The context document could not be uploaded.');
    }

    return uploadedFile;
  }, [trackWorkspaceOperation]);

  const addFiles = useCallback(async (workspaceId: string, files: File[]): Promise<string> => {
    const tooLarge = files.filter((file) => file.size > MAX_CONTEXT_FILE_BYTES);
    let failed = 0;

    for (const file of files.filter((entry) => entry.size <= MAX_CONTEXT_FILE_BYTES)) {
      const id = createEntryId();

      try {
        const storedFile = await uploadFile(workspaceId, id, file);

        update(workspaceId, (entries) => [
          ...entries,
          {
            id,
            section: 'documentation',
            text: storedFile.name,
            file: storedFile,
            createdAt: new Date().toISOString()
          }
        ]);
      } catch (error) {
        console.error('Failed to store a context document:', error);
        failed += 1;
      }
    }

    if (tooLarge.length > 0) {
      return `${tooLarge.map((file) => file.name).join(', ')} ${tooLarge.length === 1 ? 'is' : 'are'} larger than 25 MB.`;
    }

    return failed > 0 ? 'Some documents could not be added. Please try again.' : '';
  }, [update, uploadFile]);

  const openFile = useCallback(async (workspaceId: string, entry: AppContextEntry) => {
    const response = await fetch(
      `${API_BASE_PATH}/workspaces/${encodeURIComponent(workspaceId)}/context/documents/${encodeURIComponent(entry.id)}`,
      { credentials: 'include' }
    );

    if (!response.ok) {
      throw await getResponseError(response, 'The context document could not be opened.');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';

    if (!/^(application\/pdf|text\/|image\/)/.test(entry.file?.type ?? '')) {
      link.download = entry.text;
    }

    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, []);

  const clearWorkspace = useCallback((workspaceId: string) => {
    const next = { ...contextRef.current };

    delete next[workspaceId];
    contextRef.current = next;
    setContext(next);
    loadedWorkspaceIdsRef.current.delete(workspaceId);
    saveQueuesRef.current.delete(workspaceId);
    setReadyWorkspaceIds((current) => {
      const remaining = new Set(current);

      remaining.delete(workspaceId);
      return remaining;
    });
    setErrors((current) => {
      const remaining = { ...current };

      delete remaining[workspaceId];
      return remaining;
    });
  }, []);

  return { getEntries, getError, isReady, addEntry, updateEntry, removeEntry, addFiles, openFile, clearWorkspace };
}
